// frontend/src/pages/PricingPage/PricingCalculator.jsx

import React, { useState, useMemo, useEffect } from 'react';
import { 
    Box, Typography, Slider, Paper, Grid, MenuItem, Select, FormControl, 
    InputLabel, Divider, Tab, Tabs, Switch, Tooltip, IconButton, Checkbox, ListItemText, TextField, InputAdornment
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ShieldIcon from "@mui/icons-material/Shield";
import ClearIcon from '@mui/icons-material/Clear';
import ReplayIcon from '@mui/icons-material/Replay';

// --- STYLING CONSTANTS ---
const STYLES = {
    platform: { accentColor: '#ffffff' },
    activation: { accentColor: 'var(--secondary-color)' }
};

const PricingCalculator = ({ pricingConfig }) => {
    const { t } = useTranslation();
    const [tabIndex, setTabIndex] = useState(0);

    // --- DEFAULT STATE CONSTANTS ---
    const DEFAULTS = {
        volume: 1,
        contentType: 'image',
        tasks: ['ai_detection', 'fake_news_detection'],
        addons: { explanation: true, regions: true },
        activationType: 'cpm',
        licenseDays: 30,
        impressions: 100000,
        str: 0.03,
        ctr: 0.05,
        manualCpmInput: ''
    };

    // --- State Initialization ---
    const [volume, setVolume] = useState(DEFAULTS.volume);
    const [contentType, setContentType] = useState(DEFAULTS.contentType); 
    const [tasks, setTasks] = useState(DEFAULTS.tasks);
    const [addons, setAddons] = useState(DEFAULTS.addons);
    const [activationType, setActivationType] = useState(DEFAULTS.activationType); 
    const [licenseDays, setLicenseDays] = useState(DEFAULTS.licenseDays);
    const [impressions, setImpressions] = useState(DEFAULTS.impressions); 
    const [str, setStr] = useState(DEFAULTS.str); 
    const [ctr, setCtr] = useState(DEFAULTS.ctr); 
    const [manualCpmInput, setManualCpmInput] = useState(DEFAULTS.manualCpmInput);

    const isManualCpm = manualCpmInput !== '';

    // --- RESET HANDLER ---
    const handleReset = () => {
        setVolume(DEFAULTS.volume);
        setContentType(DEFAULTS.contentType);
        setTasks(DEFAULTS.tasks);
        setAddons(DEFAULTS.addons);
        setActivationType(DEFAULTS.activationType);
        setLicenseDays(DEFAULTS.licenseDays);
        setImpressions(DEFAULTS.impressions);
        setStr(DEFAULTS.str);
        setCtr(DEFAULTS.ctr);
        setManualCpmInput(DEFAULTS.manualCpmInput);
        setTabIndex(0); // Optional: Reset to first tab
    };

    // --- Handlers ---
    const handleAddonChange = (key) => (e) => {
        setAddons(prev => ({ ...prev, [key]: e.target.checked }));
    };

    const handleTasksChange = (event) => {
        const { target: { value } } = event;
        setTasks(typeof value === 'string' ? value.split(',') : value);
    };

    // Auto-switch Activation Model based on Content Type
    useEffect(() => {
        if (contentType === 'audio' || contentType === 'text') {
            setActivationType('license');
        } else {
            // Image/Video defaults to Shield (CPM) as requested
            setActivationType('cpm');
        }
    }, [contentType]);

    // Handle Impressions Input (Sync Slider & Text)
    const handleImpressionsChange = (e, newValue) => {
        // Slider change
        setImpressions(newValue);
    };
    
    // --- Logic Checks ---
    const isRegionsDisabled = contentType === 'audio';

    // --- Calculation Logic ---
    const calculation = useMemo(() => {
        if (!pricingConfig || !pricingConfig.prices) {
            return { 
                platform: "0.00", activation: "0.00", total: "0.00", units: "0", 
                platformBreakdown: {}, activationBreakdown: {}
            };
        }

        const prices = pricingConfig.prices;
        const unitRatio = pricingConfig.billing_meta.unit_to_currency_ratio; 

        // 1. PLATFORM COST
        const baseCostPerTask = prices.analysis_new[contentType] || 0;
        
        // Base Tasks Cost
        const totalBaseUnits = (baseCostPerTask * tasks.length) * volume;
        const baseCostUSD = totalBaseUnits * unitRatio;
        
        // Addons Cost
        let explanationUnits = 0;
        if (addons.explanation) explanationUnits = (prices.analysis_addons.explanation * tasks.length) * volume;
        
        let regionUnits = 0;
        if (addons.regions && !isRegionsDisabled) regionUnits = (prices.analysis_addons.regions * tasks.length) * volume;
        
        const explanationCostUSD = explanationUnits * unitRatio;
        const regionCostUSD = regionUnits * unitRatio;
        
        const totalPlatformUnits = totalBaseUnits + explanationUnits + regionUnits;
        const platformCostUSD = totalPlatformUnits * unitRatio;

        // 2. ACTIVATION COST
        let totalActivationUnits = 0;
        let activationBreakdown = {};
        
        const totalImpressions = isManualCpm ? parseInt(manualCpmInput || '0', 10) : impressions;

        if (activationType === 'license') {
            const licenseType = (contentType === 'video' || contentType === 'image') ? contentType : 'image';
            const baseFee = prices.content_license[`${licenseType}_base_fee`] || 2000;
            const dailyFee = prices.content_license[`${licenseType}_daily_fee`] || 100;
            const scanFee = prices.verification_scan_fee?.base_cost || 100; 

            // Breakdown values (Total across all volumes)
            const totalBaseFeeUnits = baseFee * volume;
            const totalDailyFeeUnits = (dailyFee * licenseDays) * volume;
            
            const estimatedScans = Math.round(totalImpressions * (str / 100));
            const totalScanUnits = estimatedScans * scanFee;

            totalActivationUnits = totalBaseFeeUnits + totalDailyFeeUnits + totalScanUnits;

            activationBreakdown = {
                base: (totalBaseFeeUnits * unitRatio).toFixed(2),
                daily: (totalDailyFeeUnits * unitRatio).toFixed(2),
                scans: (totalScanUnits * unitRatio).toFixed(2),
                scanCount: estimatedScans.toLocaleString()
            };
        } else {
            // CPM
            const cpmType = (contentType === 'video' || contentType === 'image') ? contentType : 'image';
            const cpmRate = prices.impression_cpm[cpmType] || 100;
            const clickFee = prices.verification_access?.base_cost || 2; 

            const estimatedClicks = Math.round(totalImpressions * (ctr / 100));

            const impressionUnits = (totalImpressions / 1000) * cpmRate;
            const clickUnits = estimatedClicks * clickFee;
            
            totalActivationUnits = impressionUnits + clickUnits;
            
            activationBreakdown = {
                impressions: (impressionUnits * unitRatio).toFixed(2),
                clicks: (clickUnits * unitRatio).toFixed(2),
                clickCount: estimatedClicks.toLocaleString()
            };
        }
        
        const activationCostUSD = totalActivationUnits * unitRatio;

        // 3. Totals
        const totalUnits = totalPlatformUnits + totalActivationUnits;
        const totalCostUSD = platformCostUSD + activationCostUSD;

        return {
            platform: platformCostUSD.toFixed(2),
            activation: activationCostUSD.toFixed(2),
            total: totalCostUSD.toFixed(2),
            units: Math.round(totalUnits).toLocaleString(),
            perItem: volume > 0 ? (platformCostUSD / volume).toFixed(3) : "0.000",
            
            // Breakdowns
            platformBreakdown: {
                base: baseCostUSD.toFixed(2),
                explanation: explanationCostUSD.toFixed(2),
                regions: regionCostUSD.toFixed(2)
            },
            activationBreakdown
        };
    }, [
        pricingConfig, contentType, volume, tasks, addons, isRegionsDisabled, 
        activationType, licenseDays, impressions, str, ctr, manualCpmInput, isManualCpm
    ]);

    if (!pricingConfig || !pricingConfig.prices) return null;

    const activeStyle = tabIndex === 0 ? STYLES.platform : STYLES.activation;
    const cardClass = tabIndex === 0 ? "pricing-card analysis-card" : "pricing-card activation-card";

    const availableTasks = [
        { value: 'ai_detection', label: t('selector.analysisTasks.aiDetection') },
        { value: 'fake_news_detection', label: t('selector.analysisTasks.fakeNewsDetection') }
    ];

    return (
        <Paper 
            elevation={0}
            className={`${cardClass} calculator-card-no-hover`}
            sx={{ 
                p: 4, mt: 6, height: 'auto', transition: 'all 0.3s ease',
                '&:hover': { transform: 'none !important' }
            }}
        >
            <Typography variant="h4" component="h2" sx={{ mb: 1, fontWeight: 600, textAlign: 'center', color: 'rgba(255,255,255,1)' }}>
                {t('pricingPage.calculator.title')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
                {t('pricingPage.calculator.subtitle')}
            </Typography>

            <Tabs 
                value={tabIndex} 
                onChange={(e, val) => setTabIndex(val)} 
                centered 
                sx={{ 
                    mb: 4, 
                    // 1. Default Tab Text Color (Inactive)
                    '& .MuiTab-root': { 
                        color: 'rgba(255,255,255,0.6)', 
                        textTransform: 'none', 
                        fontSize: '1rem',
                        transition: 'color 0.3s ease'
                    },
                    
                    // 2. Specific Style for Second Tab (Activation Usage) - ALWAYS BLUE TEXT
                    '& .MuiTab-root:nth-of-type(2)': {
                        color: 'var(--secondary-color)',
                        opacity: tabIndex === 1 ? 1 : 0.7 
                    },
                    
                    // 3. Selected State overrides
                    '& .Mui-selected': { 
                        fontWeight: 700,
                        // Platform (0) = White, Activation (1) = Blue
                        color: tabIndex === 0 ? '#ffffff !important' : 'var(--secondary-color) !important'
                    },
                    
                    // 4. Indicator Line Color - THIS IS THE FIX
                    '& .MuiTabs-indicator': { 
                        // Explicitly set background color based on tabIndex state
                        backgroundColor: tabIndex === 0 ? '#ffffff !important' : 'var(--secondary-color)', 
                        height: '3px', 
                        transition: 'all 0.3s ease' 
                    }
                }}
            >
                <Tab label={t('pricingPage.sections.analysis.usageType')} />
                <Tab label={t('pricingPage.sections.licensing.usageType')} />
            </Tabs>

            <Grid container spacing={6} alignItems="flex-start">
                
                {/* --- INPUT COLUMN --- */}
                <Grid item xs={12} md={6}>
                    {tabIndex === 0 ? (
                        /* --- TAB 1: PLATFORM USAGE --- */
                        <>
                            {/* 1. Content Type Selector */}
                            <FormControl fullWidth variant="outlined" sx={{ mb: 3 }} className="selector-mimic">
                                <InputLabel id="content-type-label" sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-focused': { color: 'rgba(255,255,255,1)' } }}>{t('selector.contentType.label')}</InputLabel>
                                <Select
                                    labelId="content-type-label"
                                    id="content-type-select"
                                    value={contentType}
                                    onChange={(e) => setContentType(e.target.value)}
                                    label={t('selector.contentType.label')}
                                    sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.6)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: activeStyle.accentColor } }}
                                >
                                    <MenuItem value="text">{t('selector.contentType.text')}</MenuItem>
                                    <MenuItem value="image">{t('selector.contentType.image')}</MenuItem>
                                    <MenuItem value="audio">{t('selector.contentType.audio')}</MenuItem>
                                    <MenuItem value="video">{t('selector.contentType.video')}</MenuItem>
                                </Select>
                            </FormControl>

                            {/* 2. Task Multi-Select */}
                            <FormControl fullWidth variant="outlined" sx={{ mb: 3 }} className="selector-mimic">
                                <InputLabel id="tasks-label" sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-focused': { color: 'rgba(255,255,255,1)' } }}>{t('selector.analysisTasks.label')}</InputLabel>
                                <Select
                                    multiple
                                    labelId="tasks-label"
                                    id="tasks-select"
                                    value={tasks}
                                    onChange={handleTasksChange}
                                    label={t('selector.analysisTasks.label')}
                                    renderValue={(selected) => selected.map(val => val === 'ai_detection' ? t('selector.analysisTasks.short.ai') : t('selector.analysisTasks.short.fakeNews')).join(', ')}
                                    sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.6)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: activeStyle.accentColor } }}
                                >
                                    {availableTasks.map((task) => (
                                        <MenuItem key={task.value} value={task.value}>
                                            <Checkbox checked={tasks.indexOf(task.value) > -1} />
                                            <ListItemText primary={task.label} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* 3. Switchers */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Tooltip title={t('selector.options.includeExplanation.tooltip')} placement="left"><IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}><InfoIcon fontSize="small" /></IconButton></Tooltip>
                                    <Switch
                                        className="custom-explanation-switch"
                                        inputProps={{ 'aria-label': t('selector.options.includeExplanation.label') }}
                                        checked={addons.explanation}
                                        onChange={handleAddonChange('explanation')}
                                        name="relativeMarginsSwitch"
                                        id="relative-margins-switch"
                                        sx={{
                                            '& .Mui-checked': {
                                                color: '#ffffff !important', // Thumb color when checked (White)
                                            },
                                            '& .Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#ffffff !important', // Track color when checked (White)
                                                opacity: 0.5,
                                            }
                                        }}
                                    />
                                    <Box><Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>{t('selector.options.includeExplanation.label')}</Typography></Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, opacity: isRegionsDisabled ? 0.5 : 1 }}>
                                    <Tooltip title={isRegionsDisabled ? t('selector.options.inspectableRegions.tooltip_disabled', { supportedTypes: 'Image, Video, Text' }) : t('selector.options.inspectableRegions.subtext')} placement="left"><IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}><InfoIcon fontSize="small" /></IconButton></Tooltip>
                                    <Switch
                                        className="custom-explanation-switch"
                                        inputProps={{ 'aria-label': t('selector.options.inspectableRegions.label') }}
                                        checked={addons.regions && !isRegionsDisabled}
                                        onChange={handleAddonChange('regions')}
                                        name="relativeMarginsSwitch"
                                        id="relative-margins-switch"
                                        sx={{
                                            '& .Mui-checked': {
                                                color: '#ffffff !important', // Thumb color when checked (White)
                                            },
                                            '& .Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#ffffff !important', // Track color when checked (White)
                                                opacity: 0.5,
                                            }
                                        }}
                                    />
                                    <Box><Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>{t('selector.options.inspectableRegions.label')}</Typography></Box>
                                </Box>
                            </Box>

                            {/* 4. Volume */}
                            <Typography id="volume-slider-label" gutterBottom sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>{t('pricingPage.calculator.labels.volume')}: <strong>{volume}</strong> {t('pricingPage.calculator.labels.volumeUnit')}</Typography>
                            <Slider aria-label={t('pricingPage.calculator.labels.volume')} value={volume} min={1} max={10} step={1} onChange={(e, val) => setVolume(val)} sx={{ color: activeStyle.accentColor }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 3, fontStyle: 'italic' }}>{t('selector.costEstimator.cacheNote', { percent: 90 })}</Typography>
                        </>
                    ) : (
                        /* --- TAB 2: ACTIVATION USAGE --- */
                        <>
                            {/* 1. Activation Model Selector */}
                            <FormControl id="activation-model-label" fullWidth variant="outlined" sx={{ mb: 4 }} className="selector-mimic">
                                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-focused': { color: 'var(--secondary-color)' } }}>{t('pricingPage.calculator.labels.activationModel')}</InputLabel>
                                <Select
                                    labelId="activation-model-label"
                                    id="activation-model-select"
                                    value={activationType}
                                    onChange={(e) => setActivationType(e.target.value)}
                                    label={t('pricingPage.calculator.labels.activationModel')}
                                    sx={{ 
                                        color: 'white', 
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, 
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.6)' }, 
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: activeStyle.accentColor } 
                                    }}
                                >
                                    <MenuItem value="license">{t('pricingPage.calculator.labels.modelLicense')}</MenuItem>
                                    {['image', 'video'].includes(contentType) && <MenuItem value="cpm">{t('pricingPage.calculator.labels.modelCpm')}</MenuItem>}
                                </Select>
                            </FormControl>

                            {/* 2. Unified Impressions Row */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography gutterBottom sx={{ color: 'rgba(255,255,255,0.9)', mb: 0 }}>
                                    {t('pricingPage.calculator.labels.totalImpressions')}
                                </Typography>
                                <TextField
                                    value={
                                        // Show manual input if typing, otherwise formatted slider value
                                        isManualCpm ? manualCpmInput : (impressions || 0).toLocaleString()
                                    }
                                    onChange={(e) => {
                                        const rawVal = e.target.value.replace(/,/g, '');
                                        if (rawVal === '' || /^\d+$/.test(rawVal)) {
                                            setManualCpmInput(rawVal);
                                            // Update slider state immediately if valid number, else 0
                                            setImpressions(rawVal === '' ? 0 : parseInt(rawVal, 10));
                                        }
                                    }}
                                    size="small"
                                    placeholder="e.g. 10,000"
                                    sx={{ 
                                        width: '140px',
                                        input: { 
                                            color: 'white', 
                                            textAlign: 'right', 
                                            fontWeight: 'bold',
                                            padding: '4px 8px'
                                        },
                                        // --- LABEL COLOR FIX APPLIED HERE ---
                                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: 'var(--secondary-color)' },
                                        // ------------------------------------
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                            '&:hover fieldset': { borderColor: activeStyle.accentColor },
                                            '&.Mui-focused fieldset': { borderColor: activeStyle.accentColor }
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: isManualCpm && (
                                            <InputAdornment position="end">
                                                <Tooltip title={t('pricingPage.calculator.labels.clearManual')}>
                                                    <IconButton 
                                                        onClick={() => {
                                                            setManualCpmInput('');
                                                            setImpressions(100000); // Reset to default
                                                        }} 
                                                        edge="end" 
                                                        size="small" 
                                                        sx={{ color: 'rgba(255,255,255,0.5)', padding: '2px' }}
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>

                            <Slider
                                aria-label={t('pricingPage.calculator.labels.totalImpressions')}
                                value={typeof impressions === 'number' ? impressions : 0}
                                min={10000}
                                max={10000000} // Slider cap (10M for easier sliding)
                                step={10000}
                                onChange={handleImpressionsChange}
                                sx={{ color: activeStyle.accentColor, mb: 4, width: '100%' }}
                            />

                            {activationType === 'license' ? (
                                <>
                                    {/* Duration */}
                                    <Typography gutterBottom sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        {t('pricingPage.calculator.labels.duration')}: <strong>{licenseDays}</strong> {t('pricingPage.calculator.labels.days')}
                                    </Typography>
                                    <Slider value={licenseDays} min={1} max={365} onChange={(e, val) => setLicenseDays(val)} sx={{ color: activeStyle.accentColor, mb: 4 }} />
                                    
                                    {/* STR */}
                                    <Typography gutterBottom sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        {t('pricingPage.calculator.labels.str')}: <strong>{str}%</strong>
                                    </Typography>
                                    <Slider value={str} min={0} max={0.2} step={0.01} onChange={(e, val) => setStr(val)} sx={{ color: activeStyle.accentColor }} />
                                </>
                            ) : (
                                <>
                                    {/* CTR */}
                                    <Typography gutterBottom sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        {t('pricingPage.calculator.labels.ctr')}: <strong>{ctr}%</strong>
                                    </Typography>
                                    <Slider value={ctr} min={0} max={0.2} step={0.01} onChange={(e, val) => setCtr(val)} sx={{ color: activeStyle.accentColor }} />
                                </>
                            )}
                        </>
                    )}
                </Grid>

                {/* --- RESULT COLUMN --- */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ p: 4, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: '16px', textAlign: 'center', border: `1px solid ${activeStyle.accentColor}`, boxShadow: `0 0 20px rgba(0,0,0,0.2)`, position: 'relative' }}>
                        {/* --- RESET BUTTON (Top Right) --- */}
                        <Tooltip title={t('pricingPage.calculator.labels.resetCalculator')} placement="top">
                            <IconButton 
                                onClick={handleReset}
                                sx={{ 
                                    position: 'absolute', 
                                    top: 16, 
                                    right: 16, 
                                    color: 'rgba(255,255,255,0.4)',
                                    '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                                size="small"
                            >
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {/* Existing Featured Icon (Moved slightly left or handled via conditional rendering if conflict) */}
                        {/* Since reset button is small, we can keep the featured icon but maybe lower its opacity or move it slightly if they overlap visually. 
                            Actually, let's render the featured icon BEHIND the text as a watermark, or just beside the reset button.
                            For now, let's put the featured icon to the LEFT of the Reset button. */}
                        {tabIndex === 1 && (
                            <Box sx={{ position: 'absolute', top: 16, left: 16, color: 'rgba(255,255,255,0.7)', pointerEvents: 'none' }}>
                                {activationType === 'license' ? <QrCodeIcon sx={{ fontSize: 40 }} /> : <ShieldIcon sx={{ fontSize: 40 }} />}
                            </Box>
                        )}

                        <Typography variant="subtitle2" component="h3" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1, mb: 2, mt: 1 }}>
                            {t('pricingPage.calculator.result.title')}
                        </Typography>
                        
                        <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, color: 'white' }}>${calculation.total}</Typography>

                        <Box sx={{ mt: 3, mb: 2, textAlign: 'left' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{t('pricingPage.sections.analysis.usageType')}:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>${calculation.platform}</Typography>
                            </Box>
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{t('pricingPage.sections.licensing.usageType')}:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>${calculation.activation}</Typography>
                            </Box>

                            {/* --- TAB SPECIFIC BREAKDOWN --- */}
                            <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed rgba(255,255,255,0.2)' }}>
                                {tabIndex === 0 ? (
                                    // PLATFORM BREAKDOWN
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                {volume} x {t('pricingPage.calculator.labels.baseAnalysisLabel', { count: tasks.length })}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.platformBreakdown.base}</Typography>
                                        </Box>
                                        {addons.explanation && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{volume} x {t('selector.options.includeExplanation.label')}:</Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.platformBreakdown.explanation}</Typography>
                                            </Box>
                                        )}
                                        {addons.regions && !isRegionsDisabled && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{volume} x {t('selector.options.inspectableRegions.label')}:</Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.platformBreakdown.regions}</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                                                {t('pricingPage.calculator.result.perItem', { cost: calculation.perItem })}
                                            </Typography>
                                        </Box>
                                    </>
                                ) : (
                                    // ACTIVATION BREAKDOWN
                                    <>
                                        {activationType === 'license' ? (
                                            <>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                        {t('pricingPage.calculator.labels.xBaseFee', { volume })}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.activationBreakdown.base}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                        {t('pricingPage.calculator.labels.xDailyFee', { volume, days: licenseDays })}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.activationBreakdown.daily}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                        {t('pricingPage.calculator.labels.estScansBreakdown', { count: calculation.activationBreakdown.scanCount })}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.activationBreakdown.scans}</Typography>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                        {t('pricingPage.calculator.labels.cpmImpressionsLabel')}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.activationBreakdown.impressions}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                        {t('pricingPage.calculator.labels.estClicksBreakdown', { count: calculation.activationBreakdown.clickCount })}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>${calculation.activationBreakdown.clicks}</Typography>
                                                </Box>
                                            </>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                        
                        <Divider sx={{ my: 3, bgcolor: 'rgba(255,255,255,0.1)' }} />
                        <Typography variant="h6" component="p" sx={{ color: activeStyle.accentColor, fontWeight: 500 }}>
                            {t('pricingPage.calculator.result.units', { count: calculation.units })}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default PricingCalculator;